/* ================= HdsTrackMeta (v1.2 RC) ================= */

class HdsTrackMeta
{
  static get _ver() { return '1.2 RC'; }
  static get _verdate() { return '2026-01-01'; }

  verbose = false;

  log_f(...args) {
    if (!this.verbose) return false;
    console.log(...args);
    return false; // always false for fast return
  }

  warn_f(...args) {
    if (!this.verbose) return false;
    console.warn(...args);
    return false; // always false for fast return
  }

  isMetaDomain(url) {
    if (!url) return false;
    let host;
    try {
      host = new URL(url).hostname.toLowerCase();
    } catch {
      return false;
    }

    const EXACT_HOSTS = new Set([
      'facebook.com',
      'fb.com',
      'instagram.com',
      'messenger.com',
      'm.me',
      'whatsapp.com',
      'wa.me',
    ]);
    if (EXACT_HOSTS.has(host)) return true;

    const WILDCARD_HOSTS = [
      '.facebook.com',
      '.instagram.com',
      '.whatsapp.com',
    ];
    for (let i = 0; i < WILDCARD_HOSTS.length; i++)
      if (host.endsWith(WILDCARD_HOSTS[i]))
        return true;

    return false;
  }

  constructor(pixel_id, autostart, options = {}) {
    if (!pixel_id) {
      this.warn_f('HdsTrackMeta: param `pixel-id` is required');
      return;
    }
    this.pixel_id = pixel_id;

    this.opt = Object.assign(
      {
        marketingCategory: 3,       // TrustArc marketing category ID (legacy)
        referrer: null,
        readyPollIntervalMs: 50,
        readyPollMaxTries: 200,     // ~10s
        decisionTimeoutMs: 10000,
        listenUpdates: false,       // keep listener to react to consent changes
        onDecision: null,
        onError: null
      },
      options
    );

    this.verbose = !!this.opt.referrer;
    this.opt.referrer = this.opt.referrer || document.referrer;

    this._started = false;
    this._destroyed = false;
    this._decision_resolved = false;

    this._readyPollTimer = null;
    this._decisionTimer = null;

    this._onMessage = this._onMessage.bind(this);

    if (autostart) this.start();
  }

  /* Load the Meta tracking JS - only call when all 3 criteria met. */
  activate() {
    {
      const f = window, b = document, e = 'script', v = 'https://connect.facebook.net/en_US/fbevents.js';
      let n, t, s;
      if (f.fbq) return this.log_f('HdsTrackMeta: already activated') || true;
      n = f.fbq = function(){ n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
      console.log("Meta pixel activated");
    }

    fbq('init', this.pixel_id);
    fbq('track', 'PageView');
    return true;
  }

  /**
   * Begin the flow: wait for API, request decision, and (optionally) continue to listen for updates.
   */
  async start() {
    if (this._started || this._destroyed) return;
    this._started = true;

    try {
      await this._waitForPrivacyManagerAPI();
      const decision = await this._requestConsentDecisionCore(false);

      // Handle decision from TrustArc (legacy CMP)
      this.onDecision_TrustArc1(decision);

      // Keep listening for updates if requested
      if (this.opt.listenUpdates) {
        // Listener already attached by _requestConsentDecisionCore; keep it.
      } else {
        this._detachMessageListener();
      }
    } catch (err) {
      this._emitError(err);
    }
  }

  onDecision_TrustArc1(decision) {
    this._decision_resolved = true;

    // TrustArc legacy returns { consentDecision: [categories...] }
    const categories = Array.isArray(decision?.consentDecision)
      ? decision.consentDecision
      : [];

    if (!categories.includes(this.opt.marketingCategory)) {
      console.log("marketing consent is not given");
      return this.log_f('HdsTrackMeta: no consent from legacy CMP');
      
    }

    this.log_f('HdsTrackMeta: has consent');
    return this.apply();
  }

  apply() {
    // 2. check for a meta property referrer
    let trk_meta = false;
    const ref = this.opt.referrer;
    if (this.isMetaDomain(ref)) {
      console.log("referrer is meta domain");
      const until = Math.floor(Date.now() / 1000) + 2592000; // 30d in seconds
      trk_meta = { referrer: ref, effective_until: until };
      try { localStorage.setItem('trk_meta', JSON.stringify(trk_meta)); } catch {}
      return this.activate();
    }

    // 3. check for legitimate reason to share with meta
    let until = 0;
    try {
      trk_meta = localStorage.getItem('trk_meta');
    } catch {}
    if (!trk_meta) return false;

    try {
      trk_meta = JSON.parse(trk_meta);
      until = trk_meta.effective_until;
    } catch {
      return false;
    }

    const now = Date.now() / 1000;
    if (until < now) {
      try { localStorage.removeItem('trk_meta'); } catch {}
      return false;
    }

    return this.activate();
  }

  /**
   * Re-run the handshake to get a fresh decision (safe to call anytime after start()).
   */
  async refresh() {
    if (this._destroyed) return;
    try {
      await this._ensureListener();
      const d = await this._requestConsentDecisionCore(true);
      this.onDecision_TrustArc1(d); // fixed missing `this.`
    } catch (err) {
      this._emitError(err);
    }
  }

  /**
   * Clean up timers and listeners.
   */
  destroy() {
    this._destroyed = true;
    this._detachMessageListener();
    this._clearDecisionTimer();
    if (this._readyPollTimer) {
      clearInterval(this._readyPollTimer);
      this._readyPollTimer = null;
    }
  }

  /* -------------------- internal helpers -------------------- */

  _emitError(err) {
    this.warn_f('[HdsTrackMeta]', err && err.message ? err.message : err);
    if (typeof this.opt.onError === 'function') {
      try { this.opt.onError(err); } catch {}
    }
  }

  _waitForPrivacyManagerAPI() {
    return new Promise((resolve, reject) => {
      let tries = this.opt.readyPollMaxTries;

      const tick = () => {
        if (this._destroyed) {
          if (this._readyPollTimer) {
            clearInterval(this._readyPollTimer);
            this._readyPollTimer = null;
          }
          return reject(new Error('Destroyed before PrivacyManagerAPI became ready'));
        }
        if (window.PrivacyManagerAPI) {
          if (this._readyPollTimer) {
            clearInterval(this._readyPollTimer);
            this._readyPollTimer = null;
          }
          resolve();
        } else if (--tries <= 0) {
          if (this._readyPollTimer) {
            clearInterval(this._readyPollTimer);
            this._readyPollTimer = null;
          }
          reject(new Error('PrivacyManagerAPI did not initialize in time'));
        }
      };

      tick(); // immediate check
      if (!window.PrivacyManagerAPI) {
        this._readyPollTimer = setInterval(tick, this.opt.readyPollIntervalMs);
      }
    });
  }

  /**
   * Core handshake impl: attaches listener (if not already), posts the request, waits for timeout or response,
   * then calls the legacy API for the authoritative result.
   * If keepListening=false, it removes the listener immediately after resolving the first response.
   */
  _requestConsentDecisionCore(keepListening) {
    return new Promise((resolve, reject) => {
      if (this._destroyed) {
        return reject(new Error('Destroyed before requesting consent decision'));
      }

      this._ensureListener();

      // Start (or restart) decision timeout
      this._clearDecisionTimer();
      this._decisionTimer = setTimeout(() => {
        if (!keepListening) this._detachMessageListener();
        reject(new Error('Timed out waiting for consent decision'));
      }, this.opt.decisionTimeoutMs);

      // Ask CMP to share capabilities/decision via postMessage handshake
      const apiObject = {
        PrivacyManagerAPI: {
          action: 'getConsentDecision',
          timestamp: Date.now(),
          self: window.location.host
        }
      };
      try {
        window.top.postMessage(JSON.stringify(apiObject), '*');
      } catch (e) {
        if (!keepListening) this._detachMessageListener();
        this._clearDecisionTimer();
        reject(e);
      }

      // Resolve path is handled inside the _onMessage handler by calling this._resolvePendingDecision(...)
      this._pendingResolve = (decision) => {
        this._clearDecisionTimer();
        if (!keepListening) this._detachMessageListener();
        resolve(decision);
      };
      this._pendingReject = (err) => {
        this._clearDecisionTimer();
        if (!keepListening) this._detachMessageListener();
        reject(err);
      };
    });
  }

  _ensureListener() {
    if (this._hasMessageListener) return Promise.resolve();
    window.addEventListener('message', this._onMessage);
    this._hasMessageListener = true;
    return Promise.resolve();
  }

  _detachMessageListener() {
    if (this._hasMessageListener) {
      window.removeEventListener('message', this._onMessage);
      this._hasMessageListener = false;
    }
  }

  _clearDecisionTimer() {
    if (this._decisionTimer) {
      clearTimeout(this._decisionTimer);
      this._decisionTimer = null;
    }
  }

  _onMessage(e) {
    if (this._destroyed) return;

    try {
      const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      const pma = data && data.PrivacyManagerAPI;
      if (!(pma && pma.capabilities && pma.action === 'getConsentDecision')) {
        return; // Ignore non-CMP or unrelated messages
      }

      // Pull latest authoritative decision via legacy API
      let res;
      try {
        res = window.PrivacyManagerAPI
          .callApi('getGDPRConsentDecision', window.location.host);
      } catch (err) {
        if (this._pendingReject) this._pendingReject(err);
        else this._emitError(err);
        return;
      }

      // First resolution (pending Promise) OR continuous updates
      if (this._pendingResolve) {
        const fn = this._pendingResolve;
        // Clear pending handlers so repeated messages don't re-resolve
        this._pendingResolve = null;
        this._pendingReject = null;
        fn(res);
      } else {
        // Subsequent updates (when listenUpdates=true)
        this.onDecision_TrustArc1(res); // fixed missing `this.`
      }
    } catch {
      // Ignore messages that are not JSON / not in expected format
    }
  }
}

/* ================ Initialization ================ */



// Map domain TLDs to Meta Pixel IDs
const PIXEL_IDS = {
  'de': '979893452197007', // German Site ID
  'at': '772296099240053'  // Austrian Site ID
};

function getPixelId() {
  // Check browser hostname (falls back to FreeMarker variable if available)
  const hostname = (window.location.hostname || window.LIFERAY_CURRENT_DOMAIN || '').toLowerCase();

  if (hostname.endsWith('.at')) {
    return PIXEL_IDS['at'];
  }
  
  // Default to .de site ID
  return PIXEL_IDS['de'];
}

// Your Meta Pixel ID:
//const META_PIXEL_ID = '979893452197007';
const META_PIXEL_ID = getPixelId();

// =========================================================================
// ADD YOUR CONSOLE LOG HERE:
// =========================================================================
// console.log(`[Meta Pixel Debug] Hostname: "${window.location.hostname}" | Loaded Pixel ID: "${META_PIXEL_ID}"`);
// =========================================================================

// Instantiate and autostart:
const metaTracker = new HdsTrackMeta(
  META_PIXEL_ID,
  true, // autostart
  {
    // Optional settings:
    referrer: document.referrer,  // enables verbose logs if non-emptyin
    listenUpdates: false,         // set true if CMP can push decision changes
    marketingCategory: 3,         // TrustArc legacy marketing category
    onError: (err) => console.warn('MetaTracker error:', err)
  }
);

// If you prefer manual start:
// const metaTracker = new HdsTrackMeta(META_PIXEL_ID, false);
// metaTracker.start();

/* ================= HdsTrackMeta (v1.2 RC) ================= */